import ReactPDF from '@react-pdf/renderer';
import type { GetLightInvoicesResponse } from '@weft/api-definition';
import { sanitizeFilename } from '@weft/utils';
import { formatDate } from 'apps/app/src/utils/date';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useCallback } from 'react';
import { LightInvoiceStatementDocument } from '../LightInvoiceStatementDocument/LightInvoiceStatementDocument';

type UseLightInvoicesStatementsPdfDownloaderParams = {
  onFinishedDownloading?: () => void;
  accountName: string;
};

export const useLightInvoicesStatementsPdfDownloader = ({
  onFinishedDownloading,
  accountName,
}: UseLightInvoicesStatementsPdfDownloaderParams) => {
  const downloadPDFs = useCallback(async (invoices: GetLightInvoicesResponse['invoices']) => {
    const zip = new JSZip();
    const blobArray: Blob[] = [];

    for (const invoice of invoices) {
      const pdfBlob = await ReactPDF.pdf(
        <LightInvoiceStatementDocument
          invoice={invoice}
          accountName={accountName}
        />,
      ).toBlob();

      blobArray.push(pdfBlob);
    }

    blobArray.forEach((blob, index) => {
      const invoice = invoices[index];
      const invoiceDate = formatDate(invoice.billingCycle.endTime, 'MMM_YY');
      const validFileName = sanitizeFilename(`${invoice.customer.name}_${invoiceDate}`);

      zip.file(`${validFileName}.pdf`, blob);
    });

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'invoices.zip');

    onFinishedDownloading?.();
  }, [accountName, onFinishedDownloading]);

  return downloadPDFs;
};